import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ChevronDownIcon, LockIcon } from 'lucide-react';
import { EMPTY_LEAD, Lead, MATERIAL_OPTIONS, MaterialRequerido } from '../types/lead';
import { trackFormSubmit } from '../utils/analytics';
import { EMAIL_RE } from '../utils/leadStore';
import { useWhatsAppGate } from '../contexts/WhatsAppGateContext';
import { WhatsAppButton } from './WhatsAppButton';

/* Todos los campos comparten alto, ancho y estilo para verse uniformes en cualquier pantalla */
const inputClass = 'mt-1.5 block h-12 w-full min-w-0 max-w-full appearance-none rounded-md border border-steel-200 bg-white px-3.5 text-left text-navy-900 placeholder:text-steel-400 focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600/20 [&::-webkit-date-and-time-value]:text-left';
const labelClass = 'block text-sm font-semibold text-navy-800';
export function LeadForm() {
  const [lead, setLead] = useState<Lead>(EMPTY_LEAD);
  const [submitted, setSubmitted] = useState(false);
  const {
    rememberEmail
  } = useWhatsAppGate();
  const update = <K extends keyof Lead,>(key: K, value: Lead[K]) => setLead(prev => ({
    ...prev,
    [key]: value
  }));
  const successMessage = `Hola OA LOSAS, acabo de llenar el formulario de cotización.

Nombre: ${lead.nombre}
Material: ${lead.material}
Cantidad o m²: ${lead.cantidad}
Ciudad de entrega: ${lead.ciudad}
Fecha en que lo necesito: ${lead.fecha || 'Por definir'}`;
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackFormSubmit({
      source: 'formulario',
      material: lead.material,
      ciudad: lead.ciudad,
      cantidad: lead.cantidad
    });
    // Si dejó su email, no se lo volvemos a pedir al abrir WhatsApp
    if (EMAIL_RE.test(lead.email.trim())) rememberEmail(lead.email);
    setSubmitted(true);
  };
  return <section id="formulario" className="scroll-mt-16 bg-navy-900 py-10 text-white sm:py-16" data-id="element-93" data-mp="2h7ymxjvq3gl6">
      <div className="mx-auto grid max-w-content grid-cols-1 gap-6 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14" data-id="element-94" data-mp="2m1ax4oznnkue">
        <div className="min-w-0" data-id="element-95" data-mp="2qx579dld6ib4">
          <h2 className="font-display text-[1.75rem] font-bold leading-tight sm:text-4xl" data-id="element-96" data-mp="2qxx5kw6ciu53">
            Recibe la lista de precios 2026, ficha técnica y disponibilidad
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-steel-200 sm:text-base" data-id="element-97" data-mp="2hwyy23wp84hr">
            Déjanos tus datos y te lo enviamos por WhatsApp. Si tienes prisa, escríbenos directo.
          </p>
          <p className="mt-4 hidden items-start gap-2.5 text-sm text-steel-400 lg:flex" data-id="element-98" data-mp="285vwl0zuxnhy">
            <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" aria-hidden="true" data-id="element-99" data-mp="2371ey2dmm4zf" />
            Solo usamos tus datos para cotizar y dar seguimiento. No los compartimos y puedes darte de baja cuando
            quieras.
          </p>
        </div>

        <div className="min-w-0 rounded-xl bg-white p-4 text-navy-900 sm:p-7" data-id="element-100" data-mp="1u5tgnkb4145c">
          {submitted ? <div role="status" data-id="element-101" data-mp="1p9mlmmk95d26">
              <CheckCircle2Icon className="h-10 w-10 text-signal-600" aria-hidden="true" data-id="element-102" data-mp="4pr6r10phy11" />
              <h3 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl" data-id="element-103" data-mp="9obysd5t1bee">
                Gracias. Ya recibimos tus datos.
              </h3>
              <p className="mt-2 text-[15px] text-steel-600" data-id="element-104" data-mp="dqyu4o1opegc">Para acelerar tu cotización, escríbenos ahora por WhatsApp.</p>
              <WhatsAppButton message={successMessage} label="Continuar por WhatsApp" location="form_success" intent="post_formulario" size="lg" fullWidth className="mt-5" data-id="element-105" data-mp="imw8wpxaucr0" />
              <Link to="/gracias" className="mt-4 inline-block text-sm font-medium text-navy-700 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-accent-600" data-id="element-106" data-mp="3r392a72fbmf8">
                Ver el siguiente paso de tu cotización
              </Link>
            </div> : <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-107" data-mp="19xttg7458am9">
              <div className="min-w-0" data-id="element-108" data-mp="14yqo0lk942dr">
                <label className={labelClass} htmlFor="nombre" data-id="element-109" data-mp="14vkyx6bmh0i1">
                  Nombre
                </label>
                <input id="nombre" required autoComplete="name" placeholder="Tu nombre" value={lead.nombre} onChange={e => update('nombre', e.target.value)} className={inputClass} data-id="element-110" data-mp="1iqx6ieavmlgd" />
              </div>

              <div className="min-w-0" data-id="element-111" data-mp="21mx0funezz2l">
                <label className={labelClass} htmlFor="email" data-id="element-112" data-mp="21lu6teypjiiz">
                  Email <span className="font-normal text-steel-600" data-id="element-113" data-mp="1xno4qk8tdr4e">(opcional)</span>
                </label>
                <input id="email" type="email" inputMode="email" autoCapitalize="none" autoComplete="email" placeholder="tu@correo.com" value={lead.email} onChange={e => update('email', e.target.value)} className={inputClass} data-id="element-114" data-mp="7afhv0ditn4a" />
              </div>

              <div className="min-w-0" data-id="element-115" data-mp="3fi5clkttxxct">
                <label className={labelClass} htmlFor="telefono" data-id="element-116" data-mp="3ki9jcj4xrdpu">
                  Teléfono / WhatsApp
                </label>
                <input id="telefono" type="tel" inputMode="tel" required autoComplete="tel" placeholder="10 dígitos" value={lead.telefono} onChange={e => update('telefono', e.target.value)} className={inputClass} data-id="element-117" data-mp="35k8rc076hm0z" />
              </div>

              <div className="min-w-0" data-id="element-118" data-mp="uuijvgr5rkjk">
                <label className={labelClass} htmlFor="ciudad" data-id="element-119" data-mp="zsk8eh7csc2f">
                  Ciudad de entrega
                </label>
                <input id="ciudad" required autoComplete="address-level2" placeholder="Ej. León" value={lead.ciudad} onChange={e => update('ciudad', e.target.value)} className={inputClass} data-id="element-120" data-mp="xf87b9l2m7g9" />
              </div>

              <div className="min-w-0" data-id="element-121" data-mp="1h268yli6j26l">
                <label className={labelClass} htmlFor="material" data-id="element-122" data-mp="1h14jvd4bwf5v">
                  Material
                </label>
                <div className="relative" data-id="element-123" data-mp="1c3l7kir6ycgk">
                  <select id="material" value={lead.material} onChange={e => update('material', e.target.value as MaterialRequerido)} className={`${inputClass} pr-10`} data-id="element-124" data-mp="17ccvtg2e2f08">
                    {MATERIAL_OPTIONS.map(option => <option key={option} value={option} data-id="element-125" data-mp="1v1noea9l533a">
                        {option}
                      </option>)}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-[calc(50%+3px)] h-4 w-4 -translate-y-1/2 text-steel-600" aria-hidden="true" data-id="element-126" data-mp="27c6prar44h7e" />
                </div>
              </div>

              <div className="min-w-0" data-id="element-127" data-mp="2hbnr5fzc08xk">
                <label className={labelClass} htmlFor="cantidad" data-id="element-128" data-mp="2lc7mqf49b4hl">
                  Piezas o m² de losa
                </label>
                <input id="cantidad" required placeholder="Ej. 120 m² o 65 piezas" value={lead.cantidad} onChange={e => update('cantidad', e.target.value)} className={inputClass} data-id="element-129" data-mp="2q9zffbcq6umu" />
              </div>

              <div className="min-w-0 sm:col-span-2" data-id="element-130" data-mp="351rurncis8ok">
                <label className={labelClass} htmlFor="fecha" data-id="element-131" data-mp="3910ois7k86k4">
                  ¿Para cuándo lo necesitas?
                </label>
                <input id="fecha" type="date" value={lead.fecha} onChange={e => update('fecha', e.target.value)} className={inputClass} data-id="element-132" data-mp="3uaw5jlfa1a55" />
              </div>

              <label htmlFor="consentimiento" className="flex items-start gap-3 text-sm text-steel-600 sm:col-span-2" data-id="element-133" data-mp="35o3kkhqti4t">
                <input id="consentimiento" type="checkbox" required checked={lead.consentimiento} onChange={e => update('consentimiento', e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-accent-500" data-id="element-134" data-mp="3bn0vocmscghb" />
                <span data-id="element-135" data-mp="3gizk20845yhq">Acepto recibir seguimiento por WhatsApp y email sobre mi cotización.</span>
              </label>

              <button type="submit" className="min-h-[52px] rounded-md bg-accent-500 px-6 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-accent-600 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 sm:col-span-2" data-id="element-136" data-mp="2nweth1zgpqf8">
                Enviar datos y cotizar
              </button>

              <p className="flex items-start gap-2 text-xs text-steel-600 sm:col-span-2 lg:hidden" data-id="element-137" data-mp="yfj7d27bz2le">
                <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-500" aria-hidden="true" data-id="element-138" data-mp="1m5n891t4wo8p" />
                Solo usamos tus datos para cotizar. No los compartimos.
              </p>
            </form>}
        </div>
      </div>
    </section>;
}